<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	description: {
		type: String,
		default: '',
	},
	image: {
		type: [String, Object, File, Array],
		default: null,
	},
	multiple: {
		type: Boolean,
		default: false,
	},
	showDescription: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits(['update:description', 'update:image']);

const uploadInputId = `upload-images-lucahub-${Math.random()
	.toString(36)
	.slice(2)}`;

const normalizeImages = (val) => {
	if (!val) return [];
	return Array.isArray(val) ? [...val] : [val];
};

const localImages = ref(normalizeImages(props.image));
const localImage = ref(
	props.multiple
		? null
		: props.image
		? Array.isArray(props.image)
			? props.image[0]
			: props.image
		: null
);
const localDescription = ref(props.description ?? '');

watch(
	() => props.description,
	(val) => {
		if (val !== localDescription.value) {
			localDescription.value = val ?? '';
		}
	}
);

watch(
	() => props.image,
	(val) => {
		if (props.multiple) {
			localImages.value = normalizeImages(val);
		} else if (val !== localImage.value) {
			localImage.value = Array.isArray(val) ? val[0] : val;
		}
	}
);

watch(localDescription, (val) => {
	emit('update:description', val);
});

const openModelUpload = () => {
	const target = document.getElementById(uploadInputId);
	target ? target.click() : '';
};

const uploadLucahub = (event) => {
	const fileUploads = Array.from(event?.target?.files || []);
	if (!fileUploads.length) {
		return;
	}
	if (props.multiple) {
		localImages.value = [...localImages.value, ...fileUploads];
		emit('update:image', localImages.value);
	} else {
		const file = fileUploads[0];
		localImage.value = file;
		emit('update:image', file);
	}
	event.target.value = null;
};

const getUrl = (file) => {
	if (!file) return '';
	if (typeof file === 'string') return file;
	if (file?.asset_url || file?.url) {
		return file?.asset_url || file?.url || '';
	}
	return URL.createObjectURL(file);
};

const removeImage = (index) => {
	if (props.multiple) {
		localImages.value = localImages.value.filter((_, i) => i !== index);
		emit('update:image', localImages.value);
	} else {
		localImage.value = null;
		emit('update:image', null);
	}
};
</script>
<template>
	<input
		type="file"
		hidden
		:id="uploadInputId"
		@change="uploadLucahub"
		accept="image/png, image/jpeg, image/jpg"
		:multiple="props.multiple"
	/>
	<div class="bg-white rounded-lg shadow-sm mb-10">
		<div class="divine-line py-4 font-semibold text-neutral-600">
			<div class="px-6">{{ props.title }}</div>
		</div>
		<div class="px-6 py-8">
			<div class="flex gap-4 mb-8" v-if="props.showDescription">
				<div
					class="text-xs font-semibold text-neutral-400 uppercase w-36 flex-shrink-0"
				>
					Mô tả
				</div>
				<textarea
					rows="4"
					class="w-full"
					placeholder="Nhập nội dung giới thiệu sẽ được hiển thị trên trang Luca Hub của trung tâm"
					v-model="localDescription"
				></textarea>
			</div>
			<div class="flex gap-4">
				<div
					class="text-xs font-semibold text-neutral-400 uppercase w-36 flex-shrink-0"
				>
					Hình ảnh
				</div>
				<div class="flex-1">
					<template v-if="props.multiple">
						<div
							v-if="localImages.length"
							class="flex flex-col gap-4"
						>
							<div class="grid grid-cols-5 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" >
								<div
									v-for="(img, index) in localImages"
									:key="index"
									class="rounded-lg relative overflow-hidden"
									style="height: 144px"
								>
									<div
										class="absolute top-2 right-2 cursor-pointer z-10"
										@click="removeImage(index)"
									>
										<svg
											width="24"
											height="24"
											viewBox="0 0 12 12"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g opacity="0.5">
												<rect
													width="12"
													height="12"
													rx="6"
													fill="black"
												/>
											</g>
											<path
												fill-rule="evenodd"
												clip-rule="evenodd"
												d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
												fill="white"
											/>
										</svg>
									</div>
									<img
										:src="getUrl(img)"
										alt=""
										class="w-full h-full object-cover rounded-lg"
									/>
								</div>
							</div>
							<button
								type="button"
								class="flex items-center gap-2 text-yellow-700 font-semibold w-max text-sm mt-4"
								@click="openModelUpload()"
							>
								<img
									src="@/assets/images/icons/cloud-upload-fill.png"
									alt=""
									class="w-4 h-4"
								/>
								Tải lên ảnh
							</button>
						</div>
						<div
							v-else
							class="w-full text-neutral-500 bg-neutral-50 flex flex-col items-center justify-center text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
							@click="openModelUpload()"
						>
							<img
								src="@/assets/images/icons/cloud-upload-fill.png"
								alt=""
							/>
							<div class="font-semibold">
								Tải lên hình ảnh minh họa
							</div>
							<div class="text-sm">
								Có thể tải lên nhiều hình ảnh cùng lúc
							</div>
						</div>
					</template>
					<template v-else>
						<div
							v-if="localImage"
							class="rounded-lg relative overflow-hidden"
							style="height: 144px; width: 192px"
						>
							<div
								class="absolute top-2 right-2 cursor-pointer"
								@click="removeImage()"
							>
								<svg
									width="24"
									height="24"
									viewBox="0 0 12 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<g opacity="0.5">
										<rect
											width="12"
											height="12"
											rx="6"
											fill="black"
										/>
									</g>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
										fill="white"
									/>
								</svg>
							</div>
							<img
								:src="getUrl(localImage)"
								alt=""
								class="w-full h-full object-cover"
							/>
						</div>
						<div
							v-else
							class="w-full text-neutral-500 bg-neutral-50 flex flex-col items-center justify-center text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
							@click="openModelUpload()"
						>
							<img
								src="@/assets/images/icons/cloud-upload-fill.png"
								alt=""
							/>
							<div class="font-semibold">
								Tải lên hình ảnh minh họa
							</div>
							<div class="text-sm">
								Chỉ được tải lên 01 hình ảnh
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.divine-line {
	border-bottom: 2px solid #faf5f1;
}
</style>
